import { useEffect, useMemo, useRef } from "react";
import { FinancialProductReadDTO } from "../types/FinancialApplication";
import FinancialApplicationApiClient from "../api/financialApplicationApiClient";
import { AppToasterV2 } from "@ucl/library";
import { useAutoSave } from "./useAutoSave";
import { useSetAtom } from "jotai";
import { selectedProductDetailsAtom, SelectedProductDetail } from "../atoms/selectedProductsAtom";
import { useFinancialApplicationCoreData } from "./useFinancialApplicationCoreData";

interface DepositApplicationSelectionsHook {
  selectedProductsByTabKey: Record<string, string[]>; // tabKey -> productSlugs[]
  setSelectedProductsByTabKey: (selections: Record<string, string[]>) => void;
  totalSelectedCount: number;
  clearAllSelections: () => void;
  refetch: () => void;
  loading: boolean;
  selectedProductIds: string[];
  setSelectedProductIds: (productIds: string[]) => void;
}

export const useDepositApplicationSelections = (
  financialApplicationId: string,
  apiClient: FinancialApplicationApiClient,
  allProducts: FinancialProductReadDTO[] = []
): DepositApplicationSelectionsHook => {
  const {
    coreData,
    refreshCoreData,
    coreDataLoading: loading,
  } = useFinancialApplicationCoreData(financialApplicationId);

  // Jotai atoms
  const setSelectedProductDetails = useSetAtom(selectedProductDetailsAtom);

  // Refs for tracking user changes
  const isUserChangeRef = useRef<boolean>(false);
  const pendingUpdatesRef = useRef<Array<{ depositApplicationId: string; isActive: boolean }>>([]);

  const selectedProductIds = useMemo(() => {
    return (
      coreData?.depositApplications
        ?.filter((da) => da.isActive)
        .map((da) => da.financialProductId) ?? []
    );
  }, [coreData?.depositApplications]);

  // Convert deposit applications to UI format
  const selectedProductsByTabKey = useMemo(() => {
    if (!coreData?.depositApplications || !allProducts.length) {
      return {};
    }

    const selections: Record<string, string[]> = {};

    coreData.depositApplications.forEach((depositApp) => {
      if (depositApp.isActive) {
        const product = allProducts.find((p) => p.id === depositApp.financialProductId);
        if (product) {
          const customerSegmentKey = product.customerSegment.toLowerCase();
          const productTypeKey = productTypeToTabKey(product.productType);
          const tabKey = `${customerSegmentKey}-${productTypeKey}`;

          if (!selections[tabKey]) {
            selections[tabKey] = [];
          }
          selections[tabKey].push(product.productSlug);
        }
      }
    });

    return selections;
  }, [coreData?.depositApplications, allProducts]);

  // Update jotai atom with selected product details
  useEffect(() => {
    if (!coreData?.depositApplications || !allProducts.length) {
      setSelectedProductDetails([]);
      return;
    }

    const selectedDetails: SelectedProductDetail[] = coreData.depositApplications
      .filter((depositApp) => depositApp.isActive)
      .map((depositApp) => {
        const product = allProducts.find((p) => p.id === depositApp.financialProductId);
        return product ? { product, depositApplicationId: depositApp.id } : null;
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    setSelectedProductDetails(selectedDetails);
  }, [coreData?.depositApplications, allProducts, setSelectedProductDetails]);

  // Calculate total selected count
  const totalSelectedCount = useMemo(() => {
    return coreData?.depositApplications?.filter((da) => da.isActive).length ?? 0;
  }, [coreData?.depositApplications]);

  const updateDepositApplications = async () => {
    if (pendingUpdatesRef.current.length === 0) return;

    const updates = [...pendingUpdatesRef.current];
    pendingUpdatesRef.current = [];

    try {
      // Update each deposit application sequentially
      for (const update of updates) {
        await apiClient.toggleDepositApplicationActiveStatus(financialApplicationId, {
          depositApplicationId: update.depositApplicationId,
          isActive: update.isActive,
        });
      }
    } catch (error) {
      console.error("Failed to update deposit applications:", error);
      AppToasterV2.showFailedActionToaster("Failed to update product selection");
    } finally {
      // Refetch to ensure UI is in sync
      await refreshCoreData();
    }
  };

  // Use the auto-save hook for managing saving state
  const { markDirty } = useAutoSave({
    saveFunction: updateDepositApplications,
    debounceDelay: 0,
  });

  const setSelectedProductIds = (productIds: string[]) => {
    if (!coreData?.depositApplications || !allProducts.length) return;

    const updates: Array<{ depositApplicationId: string; isActive: boolean }> = [];

    coreData.depositApplications.forEach((depositApp) => {
      if (productIds.includes(depositApp.financialProductId) && !depositApp.isActive) {
        updates.push({
          depositApplicationId: depositApp.id,
          isActive: true,
        });
      } else if (!productIds.includes(depositApp.financialProductId) && depositApp.isActive) {
        updates.push({
          depositApplicationId: depositApp.id,
          isActive: false,
        });
      }
    });

    if (updates.length > 0) {
      pendingUpdatesRef.current.push(...updates);
      markDirty();
    }
  };

  const setSelectedProductsByTabKey = (newSelections: Record<string, string[]>) => {
    if (!coreData?.depositApplications || !allProducts.length) return;

    isUserChangeRef.current = true;

    // Calculate which deposit applications need to be updated
    const updates: Array<{ depositApplicationId: string; isActive: boolean }> = [];

    coreData.depositApplications.forEach((depositApp) => {
      const product = allProducts.find((p) => p.id === depositApp.financialProductId);
      if (product) {
        const customerSegmentKey = product.customerSegment.toLowerCase();
        const productTypeKey = productTypeToTabKey(product.productType);
        const tabKey = `${customerSegmentKey}-${productTypeKey}`;

        const shouldBeActive = newSelections[tabKey]?.includes(product.productSlug) ?? false;

        if (depositApp.isActive !== shouldBeActive) {
          updates.push({
            depositApplicationId: depositApp.id,
            isActive: shouldBeActive,
          });
        }
      }
    });

    if (updates.length > 0) {
      pendingUpdatesRef.current.push(...updates);
      markDirty();
    }
  };

  const clearAllSelections = () => {
    if (!coreData?.depositApplications) return;

    isUserChangeRef.current = true;

    const updates = coreData.depositApplications
      .filter((da) => da.isActive)
      .map((da) => ({ depositApplicationId: da.id, isActive: false }));

    if (updates.length > 0) {
      pendingUpdatesRef.current.push(...updates);
      markDirty();
    }
  };

  const refetch = () => {
    refreshCoreData();
  };

  return {
    selectedProductsByTabKey,
    setSelectedProductsByTabKey,
    totalSelectedCount,
    clearAllSelections,
    refetch,
    loading,
    selectedProductIds,
    setSelectedProductIds,
  };
};

// Helper function to convert product type to tab key
const productTypeToTabKey = (productType: string): string => {
  return productType.toLowerCase().replace(/\s+/g, "-");
};
